// Static Bedrock models configuration
// AWS now enables all foundation models by default, so we use a predefined list of recommended models
// Using inference profile IDs for on-demand throughput
export const BEDROCK_MODELS = [
  {
    id: 'us.amazon.nova-micro-v1:0',
    name: 'Amazon Nova Micro',
    provider: 'Amazon',
    description: 'Budget option - may struggle with complex contact flows (use Lite for best results)',
    costTier: 'low',
    maxTokens: 4096,
    temperature: 0.1,
    recommended: false
  },
  {
    id: 'us.amazon.nova-lite-v1:0',
    name: 'Amazon Nova Lite',
    provider: 'Amazon',
    description: 'Recommended for contact flow summarization - better instruction-following for complex algorithmic tasks',
    costTier: 'medium',
    maxTokens: 4096,
    temperature: 0.1,
    recommended: true
  },
  {
    id: 'us.amazon.nova-2-lite-v1:0',
    name: 'Amazon Nova 2 Lite',
    provider: 'Amazon',
    description: 'Latest Nova 2 Lite Recommended for contact flow summarization - better instruction-following for complex algorithmic tasks',
    costTier: 'medium',
    maxTokens: 4096,
    temperature: 0.1,
    recommended: false
  },
  {
    id: 'us.amazon.nova-pro-v1:0',
    name: 'Amazon Nova Pro',
    provider: 'Amazon',
    description: 'Advanced model for complex analysis and detailed insights',
    costTier: 'high',
    maxTokens: 8192,
    temperature: 0.1,
    recommended: false
  },
  {
    id: 'us.anthropic.claude-3-haiku-20240307-v1:0',
    name: 'Claude 3 Haiku',
    provider: 'Anthropic',
    description: 'Fast and efficient for quick analysis tasks',
    costTier: 'low',
    maxTokens: 4096,
    temperature: 0.1,
    recommended: false
  },
  {
    id: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',
    name: 'Claude 3.5 Haiku',
    provider: 'Anthropic',
    description: 'Enhanced Haiku model with improved capabilities',
    costTier: 'low',
    maxTokens: 4096,
    temperature: 0.1,
    recommended: false
  }
];

// Fetch available models - now returns static list since all models are enabled by default
export const fetchAvailableModels = async (credentials, region) => {
  // AWS Bedrock now enables all foundation models by default
  // Return the static predefined list of recommended models
  return BEDROCK_MODELS;
};

// Get model by ID (works with both static and dynamic models)
export const getModelById = (modelId, availableModels = BEDROCK_MODELS) => {
  return availableModels.find(model => model.id === modelId);
};

// Get recommended model
export const getRecommendedModel = () => {
  return BEDROCK_MODELS.find(model => model.recommended) || BEDROCK_MODELS[0];
};

// Get models by cost tier
export const getModelsByCostTier = (tier) => {
  return BEDROCK_MODELS.filter(model => model.costTier === tier);
};

// Service-specific prompt templates
export const SERVICE_ANALYSIS_PROMPTS = {
  lambda: {
    systemPrompt: `You are an AWS Lambda expert analyst. You will analyze Lambda function data and provide factual, data-driven insights for technical stakeholders.

Focus your analysis on these 4 KEY AREAS ONLY:
1. RUNTIME DEPRECATION: Identify functions using deprecated runtimes (Block Function Create/Update status)
2. LOG RETENTION: Analyze log retention configuration across functions
3. UNUSED FUNCTIONS: Identify functions not invoked in last 12-15 months
4. ERROR RATES: Functions with high error rates in last 30 days

IMPORTANT RULES:
- Consolidate all runtime deprecation findings into a single comprehensive section
- Only analyze the 4 key areas above - ignore code size, environment variables, architecture patterns
- Use "runtime updates" not "security updates" for Lambda runtime deprecations
- All findings must reference specific functions with supporting data
- Focus on actionable insights backed by measurable data`,
    
    userPrompt: (functionData) => `Analyze the following AWS Lambda functions data and provide a comprehensive technical analysis based ONLY on the provided data:

${JSON.stringify(functionData, null, 2)}

The data provided contains summary statistics only - no individual function names. Provide your analysis in the following JSON format focusing ONLY on the 4 key areas:
{
  "executiveSummary": "Brief overview focusing on runtime deprecations, log retention, unused functions, and error rates using summary statistics",
  "metrics": {
    "totalFunctions": "from summary.totalFunctions",
    "deprecatedFunctionsCount": "from summary.deprecatedFunctions",
    "unusedFunctionsCount": "from summary.unusedFunctions", 
    "functionsWithErrorsCount": "from summary.functionsWithErrors"
  },
  "runtimeDeprecationAnalysis": {
    "summary": "Overall runtime deprecation status based on summary statistics",
    "deprecationBreakdown": "directly copy the summary.runtimeDeprecationBreakdown object showing each runtime with its function count (e.g., 'python3.8 (Create&Update)': 1)",
    "totalDeprecatedFunctions": "from summary.deprecatedFunctions"
  },
  "logRetentionAnalysis": {
    "summary": "Log retention configuration analysis based on available data",
    "retentionBreakdown": "directly copy the summary.logRetentionBreakdown object showing each retention period with its function count (e.g., '30 days': 5, 'Never Expire': 2)",
    "functionsWithShortRetention": "from summary.functionsWithShortLogRetention (< 30 days)",
    "functionsWithLongRetention": "from summary.functionsWithLongLogRetention (>= 30 days)", 
    "functionsWithUnknownRetention": "from summary.functionsWithUnknownLogRetention",
    "functionsWithNeverExpire": "from summary.functionsWithNeverExpire (MEDIUM priority - no retention policy set)",
    "functionsWithLogGroupNotFound": "from summary.functionsWithLogGroupNotFound (LOW priority - function never executed)",
    "generalFindings": "overview of log retention patterns and potential cost implications"
  },
  "unusedFunctionsAnalysis": {
    "summary": "Analysis of unused functions based on LastInvocationDisplay data showing functions with 'None in the last...' patterns",
    "totalUnusedFunctions": "from summary.unusedFunctions",
    "unusedFunctionsByPeriod": "directly copy the summary.unusedFunctionsByPeriod object showing each period with its function count (e.g., 'None in the last 12 months': 56, 'None in the last 6 months': 5)",
    "percentageOfTotal": "calculate (totalUnusedFunctions / totalFunctions * 100) and return as number (e.g., 25.5 not '25.5%')"
  },
  "errorRateAnalysis": {
    "summary": "Functions with error rates in last 30 days based on summary data",
    "totalFunctionsWithErrors": "from summary.functionsWithErrors",
    "errorRateDistribution": "directly copy the summary.errorRateBreakdown object showing error rate categories with their function counts (e.g., 'lowError': 3, 'mediumError': 1, 'highError': 0)"
  },
  "recommendations": [
    {
      "priority": "high|medium|low",
      "category": "runtime_update|configuration_review|monitoring_setup",
      "action": "specific action based on summary statistics",
      "dataJustification": "specific summary statistics that support this recommendation",
      "affectedCount": "number of functions affected based on summary data"
    }
  ]
}

REMEMBER: 
- Base ALL findings on actual data from the provided dataset
- Do not make unsupported recommendations
- Use "runtime updates" not "security updates" for Lambda deprecations
- Include specific function names and data values in all findings
- Do not estimate costs without billing data`
  },
  
  lexV2: {
    systemPrompt: `You are an AWS Lex V2 expert analyst. You will analyze Lex bot data and provide factual, data-driven insights for technical stakeholders.

Focus your analysis on these KEY AREAS ONLY:
1. BOT USAGE: Identify unused bots (no invocations in last 30 days from analytics data)
2. CONVERSATION PERFORMANCE: Failed/missed vs total conversations (from conversationAnalytics data)
3. UTTERANCE PERFORMANCE: Missed vs detected utterances (from conversationAnalytics data)
4. BASIC BOT INVENTORY: Count and status of bots

IMPORTANT RULES:
- Count bots correctly - do not confuse bot count with intent count
- Only analyze conversation performance if conversationAnalytics data is available
- Focus on measurable analytics data, not configuration speculation
- All findings must reference specific bots with supporting analytics data
- Do not include configuration analysis or integration insights`,
    
    userPrompt: (botData) => `Analyze the following AWS Lex V2 bots data and provide a comprehensive technical analysis based ONLY on the provided data:

${JSON.stringify(botData, null, 2)}

Please provide your analysis in the following JSON format focusing on bot analytics:
{
  "executiveSummary": "Brief overview of bot usage, conversation performance, and utterance performance based on analytics data",
  "metrics": {
    "totalBots": "Count from data.summary.totalBots - do not confuse with intents",
    "botsWithAnalytics": number,
    "unusedBots": number,
    "botsWithHighFailureRates": number
  },
  "unusedBotsAnalysis": {
    "summary": "Bots with no recent invocations",
    "unusedBots": [
      {
        "botName": "bot name",
        "analyticsAvailable": "true|false",
        "totalConversations": "from conversationAnalytics if available"
      }
    ]
  },
  "conversationPerformanceAnalysis": {
    "summary": "Analysis of failed/missed vs total conversations",
    "botsWithIssues": [
      {
        "botName": "bot name",
        "totalConversations": "from analytics data",
        "failedConversations": "from analytics data", 
        "droppedConversations": "from analytics data",
        "failureRate": "calculated percentage"
      }
    ]
  },
  "utterancePerformanceAnalysis": {
    "summary": "Analysis of missed vs detected utterances",
    "botsWithIssues": [
      {
        "botName": "bot name",
        "detectedUtterances": "from analytics data",
        "missedUtterances": "from analytics data",
        "missedUtteranceRate": "calculated percentage"
      }
    ]
  },
  "recommendations": [
    {
      "priority": "high|medium|low",
      "category": "configuration_review|analytics_setup|integration_validation",
      "action": "specific action based on data findings",
      "dataJustification": "specific data points that support this recommendation",
      "affectedBots": ["bot names with supporting data"]
    }
  ]
}

REMEMBER:
- Base ALL findings on actual data from the provided dataset
- Do not make unsupported recommendations about user experience  
- Only analyze conversation performance if conversationAnalytics data is available
- Include specific bot names and data values in all findings
- Do not estimate costs without usage data
- Bot deduplication is handled automatically - analyze unique bots only`
  },

  connect: {
    systemPrompt: `You are an Amazon Connect expert analyst. You will analyze Amazon Connect contact flow data and provide factual, data-driven insights for technical stakeholders.

Focus your analysis on these KEY AREAS ONLY:
1. CONTACT FLOW ERROR ANALYSIS: Analyze flows with errors per Connect instance
2. INSTANCE PERFORMANCE: Compare error rates across multiple instances if applicable
3. OPERATIONAL RELIABILITY: Assess impact of flow errors on customer experience
4. ERROR REMEDIATION: Provide specific recommendations for error resolution

IMPORTANT RULES:
- Analyze each Connect instance separately with clear instance identification
- Focus on ContactFlowErrors30Days data for error analysis
- If no flows have errors, clearly state this as a positive finding
- Provide actionable recommendations only when errors are present
- Do not reference specific flow names (detailed information available in Excel export)
- Base all findings on actual error count data provided`,
    
    userPrompt: (connectData) => `Analyze the following Amazon Connect contact flow data and provide a comprehensive technical analysis based ONLY on the provided data:

${JSON.stringify(connectData, null, 2)}

The data contains contact flow information organized by Connect instance. Provide your analysis in the following JSON format:
{
  "executiveSummary": "Brief overview of contact flow health across all Connect instances, highlighting error patterns and overall operational status",
  "metrics": {
    "totalInstances": "from summary.totalInstances",
    "totalContactFlows": "from summary.totalFlows", 
    "totalFlowsWithErrors": "from summary.totalFlowsWithErrors",
    "overallErrorRate": "calculate (totalFlowsWithErrors / totalContactFlows * 100) as percentage"
  },
  "instanceAnalysis": [
    {
      "instanceName": "instance name from data",
      "instanceId": "instance ID from data",
      "totalFlows": "total flows for this instance",
      "flowsWithErrors": "flows with errors for this instance",
      "errorRate": "calculate (flowsWithErrors / totalFlows * 100) as percentage",
      "status": "healthy|warning|critical based on error presence",
      "findings": "specific findings for this instance based on error data"
    }
  ],
  "recommendations": [
    {
      "priority": "high|medium|low",
      "category": "error_resolution|monitoring_setup|operational_review",
      "action": "specific action based on error analysis",
      "dataJustification": "specific error counts or patterns that support this recommendation",
      "affectedInstances": ["list of instance names with errors"],
      "affectedFlowCount": "number of flows affected"
    }
  ]
}

ANALYSIS GUIDELINES:
- If an instance has zero flows with errors, mark status as "healthy" and note "No action items needed"
- If an instance has any flows with errors, provide specific recommendations for investigation
- For multiple instances, provide comparative analysis highlighting which instances need attention
- Base error rate calculations on actual flow counts provided
- Include specific data points to justify all recommendations

REMEMBER:
- Base ALL findings on actual data from the provided dataset
- Do not reference specific contact flow names
- Focus on actionable insights for technical teams
- Clearly distinguish between instances in multi-instance scenarios`
  }
};

// Get analysis prompt for a specific service
export const getAnalysisPrompt = (serviceName, data) => {
  const promptConfig = SERVICE_ANALYSIS_PROMPTS[serviceName];
  if (!promptConfig) {
    throw new Error(`No analysis prompt configured for service: ${serviceName}`);
  }
  
  return {
    systemPrompt: promptConfig.systemPrompt,
    userPrompt: promptConfig.userPrompt(data)
  };
};